import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import { MdCheck, MdClose, MdInfo } from "react-icons/md";
import { ICON_SIZES } from "src/helpers/constants";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import clsx from "clsx";
import RadioGroup from "../RadioGroup/RadioGroup";
import CustomSelect from "../Select/CustomSelect";
import CheckBox from "../CheckBox/CheckBox";
import TextInput from "../TextInput/TextInput";
import { useId, useMemo, useState } from "react";
import useMobile from "src/hooks/useMobile";
import useCollapsibles, {
  EExportTransactionsMobileCollapsibles,
} from "src/hooks/useCollapsibles";
import InfoBlock from "../InfoBlock/InfoBlock";
import SlideDownUpAnimation from "../Animations/SlideDownUpAnimation";
import { Tooltip } from "react-tooltip";
import { EButtonKind } from "../Button/ButtonTypes";

interface IExportTransactionsModal {
  onClose: () => void;
  onConfirm: (data: any) => void;
  isLoading: boolean;
  selectedRows: number;
  totalRows: number;
}

export enum EXPORT_TYPE {
  ALL = "ALL",
  SELECTION = "SELECTION",
}

export enum FILE_TYPES {
  XLSX = "XLSX",
  PDF = "PDF",
  JSON = "JSON",
  CSV = "CSV",
}

const ExportTransactionsModal: React.FC<IExportTransactionsModal> = ({
  onClose,
  onConfirm,
  isLoading,
  selectedRows = 0,
  totalRows = 0,
}) => {
  const { t } = useTranslation();
  const {
    updateCollapsible,
    collapsiblePreferences,
    toggleExportTransactionsMobileCollapsible,
    isExportTransactionsMobileCollapsibleOpen,
  } = useCollapsibles();
  const tooltipId = useId();
  const { isMobile } = useMobile();
  const [exportType, setExportType] = useState<EXPORT_TYPE>(EXPORT_TYPE.ALL);
  const [formatNumbers, setFormatNumbers] = useState<boolean>(true);
  const [csvColumnDelimiter, setCsvColumnDelimiter] = useState<string>(",");
  const [csvRowDelimiter, setCsvRowDelimiter] = useState<string>("\\n");

  const [includeCurrencySymbol, setIncludeCurrencySymbol] =
    useState<boolean>(true);
  const [fileFormat, setFileFormat] = useState<Partial<FILE_TYPES>>(
    FILE_TYPES.XLSX
  );
  const [fileName, setFileName] = useState<string>(
    `eXpenser.net - Transacciones ${new Date()
      .toISOString()
      .replace(/[:.]/g, "-")}`
  );

  const canExport = useMemo(() => {
    const exportTypeCondition =
      exportType === EXPORT_TYPE.SELECTION ? selectedRows > 0 : totalRows > 0;
    return (
      exportType && fileFormat && fileName?.trim?.() && exportTypeCondition
    );
  }, [exportType, fileFormat, fileName]);

  return (
    <Modal
      onEscape={() => onClose()}
      className="md:w-[550px] md:min-w-[550px] md:max-w-[550px]"
    >
      <Modal.Header title="Exportar transacciones" onClose={() => onClose()} />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "gap-6", "w-full"])}>
          <CollapsibleBlock
            title="¿Qué deseas exportar?"
            open={
              isMobile
                ? isExportTransactionsMobileCollapsibleOpen(
                    EExportTransactionsMobileCollapsibles.WHAT_TO_EXPORT
                  )
                : collapsiblePreferences.exportTransactionsWhatToExportOpen
            }
            onChange={(isOpen: boolean) => {
              if (isMobile) {
                toggleExportTransactionsMobileCollapsible(
                  EExportTransactionsMobileCollapsibles.WHAT_TO_EXPORT
                );
              } else {
                updateCollapsible({
                  exportTransactionsWhatToExportOpen: isOpen,
                });
              }
            }}
          >
            <RadioGroup
              layout={isMobile ? "vertical" : "horizontal"}
              options={[
                {
                  description: "Sólo las filas seleccionadas",
                  value: EXPORT_TYPE.SELECTION,
                  label: "Selección",
                },
                {
                  description: "Todos los resultados de la tabla",
                  value: EXPORT_TYPE.ALL,
                  label: "Todo",
                },
              ]}
              selected={exportType}
              onChange={(value: any) => setExportType(value)}
            />
          </CollapsibleBlock>
          <CollapsibleBlock
            title="Opciones adicionales"
            open={
              isMobile
                ? isExportTransactionsMobileCollapsibleOpen(
                    EExportTransactionsMobileCollapsibles.ADDITIONAL_OPTIONS
                  )
                : collapsiblePreferences.exportTransactionsAdditionalOptionsOpen
            }
            onChange={(isOpen: boolean) => {
              if (isMobile) {
                toggleExportTransactionsMobileCollapsible(
                  EExportTransactionsMobileCollapsibles.ADDITIONAL_OPTIONS
                );
              } else {
                updateCollapsible({
                  exportTransactionsAdditionalOptionsOpen: isOpen,
                });
              }
            }}
          >
            <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
              <div
                className={clsx([
                  "grid",
                  "grid-cols-1",
                  "md:grid-cols-2",
                  "gap-4",
                  "w-full",
                ])}
              >
                <CheckBox
                  checked={formatNumbers}
                  onChange={(isChecked: boolean) => setFormatNumbers(isChecked)}
                  label="Números"
                  description="Formatear los números"
                />
                <CheckBox
                  checked={includeCurrencySymbol}
                  onChange={(isChecked: boolean) =>
                    setIncludeCurrencySymbol(isChecked)
                  }
                  label="Moneda"
                  description="Incluir el símbolo de moneda"
                />
              </div>
              <InfoBlock
                displayMobileIcon
                message="Las siguientes opciones se toman en cuenta únicamente para archivos en formato CSV."
              />
              <div
                className={clsx([
                  "grid",
                  "grid-cols-1",
                  "gap-4",
                  "md:grid-cols-2",
                  "w-full",
                ])}
              >
                <TextInput
                  value={csvColumnDelimiter}
                  label="Delimitador de columnas"
                  onChange={(e: any) => setCsvColumnDelimiter(e?.target?.value)}
                  required
                  placeholder="El más común es una coma: ,"
                />
                <TextInput
                  value={csvRowDelimiter}
                  label="Delimitador de filas"
                  onChange={(e: any) => setCsvRowDelimiter(e?.target?.value)}
                  placeholder="El más común es un salto de línea: \n"
                  required
                />
              </div>
            </div>
          </CollapsibleBlock>

          <CollapsibleBlock
            title="Archivo"
            open={
              isMobile
                ? isExportTransactionsMobileCollapsibleOpen(
                    EExportTransactionsMobileCollapsibles.FILE_INFO
                  )
                : collapsiblePreferences.exportTransactionsFileInfoOpen
            }
            onChange={(isOpen: boolean) => {
              if (isMobile) {
                toggleExportTransactionsMobileCollapsible(
                  EExportTransactionsMobileCollapsibles.FILE_INFO
                );
              } else {
                updateCollapsible({
                  exportTransactionsFileInfoOpen: isOpen,
                });
              }
            }}
          >
            <div
              className={clsx([
                "grid",
                "grid-cols-1",
                "md:grid-cols-2",
                "gap-4",
                "w-full",
              ])}
            >
              <CustomSelect
                onChange={(e: any) => setFileFormat(e?.value)}
                value={fileFormat}
                isClearable={false}
                useValueAsLabel={false}
                isSearchable={false}
                options={[
                  { value: FILE_TYPES.CSV, label: "CSV" },
                  { value: FILE_TYPES.XLSX, label: "Excel" },
                  // { value: FILE_TYPES.PDF, label: "PDF" },
                  { value: FILE_TYPES.JSON, label: "JSON" },
                ]}
                label="Formato"
                required
                placeholder="Selecciona un formato"
              />
              <TextInput
                label="Nombre del archivo"
                onChange={(e: any) => setFileName(e?.target?.value)}
                value={fileName}
                required
                placeholder="Nombre del archivo"
              />
            </div>
          </CollapsibleBlock>
        </div>
      </Modal.Body>
      <Modal.Footer
        leftContent={
          <div className="flex-shrink-0">
            <SlideDownUpAnimation
              show={exportType === EXPORT_TYPE.SELECTION && selectedRows === 0}
            >
              <div
                className={clsx([
                  "flex",
                  "flex-row",
                  "items-center",
                  "gap-1",
                  "flex-shrink-0",
                ])}
                data-tooltip-id={tooltipId}
                data-tooltip-content={
                  isMobile ? "No tienes filas seleccionadas" : ""
                }
                data-tooltip-position-strategy="fixed"
                data-tooltip-offset={0}
              >
                <MdInfo
                  size={ICON_SIZES.SM}
                  className={clsx([
                    "text-red-500",
                    "dark:text-purple-400",
                    "shrink-0",
                  ])}
                />
                <span
                  className={clsx([
                    "!text-xs",
                    "font-medium",
                    "hidden",
                    "md:inline",
                  ])}
                >
                  No tienes filas seleccionadas
                </span>
                {isMobile && (
                  <Tooltip
                    id={tooltipId}
                    style={{ zIndex: 99 }}
                    positionStrategy="fixed"
                  />
                )}
              </div>
            </SlideDownUpAnimation>
          </div>
        }
        actions={[
          {
            text: t("shared.export"),
            onClick: () => {
              onConfirm({
                exportType,
                includeCurrencySymbol,
                formatNumbers,
                fileName,
                fileFormat,
              });
            },
            kind: EButtonKind.SAVE,
            icon: <MdCheck size={ICON_SIZES.MD} />,
            disabled: !canExport,
          },
          {
            text: t("shared.close"),
            onClick: () => onClose(),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.MD} />,
          },
        ]}
      />
    </Modal>
  );
};

export default ExportTransactionsModal;
